import {
  ChevronDown,
  SearchSm,
} from "@liftedcare/solidjs-untitled-icons/duocolor";
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  JSXElement,
  onCleanup,
  Show,
} from "solid-js";
import { Portal } from "solid-js/web";

import { focusedWithinComponent } from "../../utils/focusedWithinElement.js";
import { IssueDisplay, useForm } from "./FormContext.js";

export type SelectValueType = number | string | boolean | null;

export interface ISelectOption<V = any, N = string> {
  value: V;
  name: N;
  group?: string;
}

interface SelectProps<T> {
  value?: T | null;
  editable?: boolean;
  name?: string;
  class?: string;
  labelClass?: string;
  label?: string | JSXElement;
  options: ISelectOption[];
  onChange?: (value: T) => void;
  error?: string;
  placeholder?: string;
  showSearchBar?: boolean;
  nullableValueName?: string;
  hideValidation?: boolean;
  disabled?: boolean;
  containerClass?: string;
  listItemClass?: string;
}

function Select<
  ValueType extends SelectValueType & Component<SelectProps<ValueType>>,
>(props: SelectProps<ValueType>) {
  const options = createMemo(() => {
    if (props.nullableValueName) {
      return [
        {
          value: null,
          name: props.nullableValueName,
        },
        ...props.options,
      ];
    }
    return props.options;
  });

  const [opened, setOpened] = createSignal(false);
  const [activeOptionIndex, setActiveOptionIndex] = createSignal<number>(-1);
  const [focused, setFocused] = createSignal(false);
  const [filterQuery, setFilterQuery] = createSignal("");

  const commonClassName = createMemo(
    () =>
      `${props.class} border rounded-md ${
        props.disabled ? "bg-gray-100" : "bg-white"
      }  ${props.editable === false ? "" : "p-2"}`,
  );
  const labelClass = createMemo(
    () => `text-gray-500  ${props.labelClass ?? ""}`,
  );
  const id = createUniqueId();
  const scrollableContainerId = createUniqueId();
  const componentId = createUniqueId();
  const searchBarId = createUniqueId();
  const formContext = useForm();

  let searchBarElm!: HTMLInputElement;
  let containerDiv!: HTMLDivElement;
  let menuElm!: HTMLDivElement;

  const displayableOptions = createMemo(() => {
    if (!filterQuery().length) {
      return options();
    }
    return options().filter((option) =>
      option.name.toLowerCase().includes(filterQuery().toLowerCase()),
    );
  });

  const isFirstInGroup = (value: unknown, group?: string) => {
    if (!options().every((option) => option.group)) {
      return false;
    }
    const index = options().findIndex(
      (option) => option.value === value && option.group === group,
    );
    return index === 0 || options()[index - 1].group !== group;
  };

  const currentValue = createMemo(() => {
    return formContext && props.name
      ? formContext.getData(props.name)
      : props.value;
  });

  const currentValueIndex = createMemo(() => {
    return options().findIndex((option) => option.value === currentValue());
  });

  const displayValue = createMemo(() => {
    const searchValue =
      formContext && props.name ? formContext.getData(props.name) : props.value;
    const displayOption = options().find(
      (option) => option.value === searchValue,
    );
    if (displayOption) {
      return displayOption.name;
    }
    return null;
  });

  const toggleSelect = () => {
    if (props.disabled) {
      return;
    }
    if (!opened()) {
      setActiveOptionIndex(currentValueIndex());
      setFilterQuery("");
    }
    setOpened(!opened());

    // set focus on the search box or back on the container
    setTimeout(() => {
      if (opened()) {
        menuElm.style.top = `${containerDiv.getBoundingClientRect().top}px`;
        menuElm.style.left = `${containerDiv.getBoundingClientRect().left}px`;
        menuElm.style.width = `${containerDiv.getBoundingClientRect().width}px`;
        menuElm.style.position = "absolute";

        // this prevents screen jank as the menu position is calculated
        menuElm.style.display = "block";

        setTimeout(() => {
          searchBarElm?.focus();
        }, 1);
      } else {
        containerDiv?.focus();
      }
    }, 1);
  };

  const valueChanged = (value: ValueType) => {
    if (formContext && props.name) {
      formContext.setData(props.name, value);
    }
    props.onChange?.(value);
    toggleSelect();
  };

  createEffect(() => {
    if (activeOptionIndex() >= displayableOptions().length) {
      setActiveOptionIndex(0);
    }
  });

  createEffect(() => {
    // signal doesn't cause effect to fire if it is passed to .item() function, store it as variable here to enforce update
    if (!opened()) {
      return;
    }

    const index = activeOptionIndex();
    const container = document.getElementById(scrollableContainerId);
    if (!container) {
      throw new Error("Container not found");
    }
    const containerChildren = [...container.querySelectorAll("*")];
    const activeOption = containerChildren
      .filter((a): a is HTMLElement => a.tagName === "DIV")
      .filter((a) => a.dataset.type === "option")
      .at(index);
    if (!activeOption) {
      return;
    }

    if (activeOption && container) {
      if (container.scrollTop > activeOption.offsetTop) {
        container.scrollTop = activeOption.offsetTop;
      }

      if (
        container.scrollTop + container.clientHeight <=
        activeOption.offsetTop
      ) {
        container.scrollTop =
          activeOption.offsetTop +
          activeOption.clientHeight -
          container.clientHeight;
      }
    }
  });

  const keyboardEventHandler = async (event: KeyboardEvent) => {
    if (event.defaultPrevented) {
      return;
    }

    if (
      (focused() || opened()) &&
      [" ", "Enter", "ArrowDown", "ArrowUp"].includes(event.key) &&
      (document.activeElement?.id !== searchBarId || event.key !== " ")
    ) {
      event.preventDefault();
    }

    // Event is fired and handled before focus change so to let the tab button switch focus to other component, delay handling in event loop
    await new Promise((r) => setTimeout(r, 1));

    if (
      !focusedWithinComponent(componentId, document.activeElement ?? event) &&
      event.target !== searchBarElm
    ) {
      setOpened(false);
      setFocused(false);
      return;
    }
    if (opened()) {
      if (event.key === "ArrowDown") {
        setActiveOptionIndex(
          Math.min(activeOptionIndex() + 1, displayableOptions().length - 1),
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        setActiveOptionIndex(Math.max(activeOptionIndex() - 1, 0));
        event.preventDefault();
      } else if ([" ", "Enter"].includes(event.key)) {
        event.preventDefault(); // Prevent window scrolling with arrows or space bar click
      }
    }
    if (focused()) {
      if ([" ", "Enter", "ArrowDown"].includes(event.key)) {
        if (document.activeElement?.id === searchBarId && event.key === " ") {
          return;
        }
        // arrow down just needs to open the select
        if (event.key === "ArrowDown" && opened()) {
          return;
        }
        if (opened()) {
          const valueIndex = displayableOptions().findIndex(
            (option) => option.value === currentValue(),
          );
          if (valueIndex !== activeOptionIndex()) {
            valueChanged(displayableOptions().at(activeOptionIndex())?.value);
            return;
          }
        }
        toggleSelect();
        event.preventDefault();
      }
      if (event.key === "Escape" && opened()) {
        toggleSelect();
      }
    }
  };

  const mouseClickEventHandler = async (event: MouseEvent) => {
    if (event.defaultPrevented) {
      return;
    }
    // Event is fired and handled before focus change so to let the tab button switch focus to other component, delay handling in event loop
    await new Promise((r) => setTimeout(r, 1));
    if (
      (focused() || opened()) &&
      !focusedWithinComponent(componentId, document.activeElement ?? event) &&
      !focusedWithinComponent(componentId, menuElm ?? event) &&
      event.target !== searchBarElm
    ) {
      setOpened(false);
      setFocused(false);
      return;
    }
  };

  document.addEventListener("keydown", keyboardEventHandler);
  document.addEventListener("click", mouseClickEventHandler);
  onCleanup(() => {
    document.removeEventListener("click", mouseClickEventHandler);
    document.removeEventListener("keydown", keyboardEventHandler);
  });

  const getFormErrors = () => {
    if (!props.name) {
      return false;
    }

    const errors = formContext?.getErrors(props.name);
    // sometimes this returns true, which means the field is valid
    if (errors && errors !== true) {
      return errors;
    }
    return formContext?.errors?.()?.[props.name] ?? false;
  };

  const relevantIssues = () => formContext?.getIssues(props.name);

  return (
    <div
      class={`relative flex flex-col ${props.containerClass}`}
      id={componentId}
    >
      <Show when={props.label}>
        <label for={id} class={labelClass()}>
          {props.label}
        </label>
      </Show>
      <Show
        when={props.editable || props.editable === undefined}
        fallback={
          <div
            class={`${commonClassName()} overflow-hidden border-transparent`}
          >
            <span
              class="w-full truncate"
              classList={{
                "text-gray-500":
                  displayValue() === null && !props.nullableValueName,
              }}
            >
              {displayValue() ?? props.placeholder}
            </span>
          </div>
        }
      >
        {/* TODO: a11y */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          ref={containerDiv!}
          class={`cursor-pointer ${commonClassName()}`}
          onClick={toggleSelect}
          // TODO: a11y
          //  eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onFocus={() => {
            setFocused(true);
          }}
          classList={{
            "border-error-500 outline-error-500":
              Boolean(getFormErrors()) || Boolean(relevantIssues()?.length),
          }}
        >
          <div
            class={`flex flex-row items-center justify-between overflow-hidden`}
          >
            <span
              class="w-full truncate"
              classList={{
                "text-gray-500":
                  displayValue() === null && !props.nullableValueName,
              }}
            >
              {displayValue() || props.placeholder}
            </span>
            <div
              class="transition duration-300"
              classList={{
                "rotate-180": opened(),
              }}
            >
              <ChevronDown class="size-5" />
            </div>
          </div>
        </div>
        <Show when={opened()}>
          <Portal>
            <div
              class="absolute z-50 hidden divide-y overflow-hidden rounded-md border bg-white shadow-md"
              ref={menuElm!}
            >
              <Show when={props.showSearchBar}>
                <div class="flex flex-row items-center p-2">
                  <SearchSm class="size-5" />
                  <input
                    id={searchBarId}
                    ref={searchBarElm!}
                    type="text"
                    data-lpignore="true"
                    class="mx-2 w-full px-1 text-lg outline-none"
                    autocomplete="off"
                    tabIndex={0}
                    onInput={(e) => setFilterQuery(e.currentTarget.value)}
                    value={filterQuery()}
                  />
                </div>
              </Show>
              <div
                class="relative flex max-h-60 w-full flex-col overflow-y-auto"
                id={scrollableContainerId}
              >
                <For each={displayableOptions()}>
                  {(option, index) => {
                    return (
                      <>
                        <Show when={isFirstInGroup(option.value, option.group)}>
                          <div data-type="group" class="border-b">
                            <p class="p-2 font-medium">{option.group}</p>
                          </div>
                        </Show>
                        {/* TODO: a11y  */}
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                        <div
                          data-type="option"
                          class={`${props.listItemClass ?? ""} cursor-pointer p-2`}
                          classList={{
                            "bg-primary-200": index() === activeOptionIndex(),
                            "bg-primary-400": option.value === currentValue(),
                          }}
                          onMouseMove={() => {
                            setActiveOptionIndex(index());
                          }}
                          onClick={() => valueChanged(option.value)}
                        >
                          {option.name}
                        </div>
                      </>
                    );
                  }}
                </For>
                <Show when={!displayableOptions().length}>
                  <div class="p-2 text-gray-500">
                    There are no options to display
                  </div>
                </Show>
              </div>
            </div>
          </Portal>
        </Show>
      </Show>
      <Show when={relevantIssues()}>
        <p class="text-sm text-red-500 lg:col-span-2 lg:col-start-2">
          <IssueDisplay issues={relevantIssues()} />
        </p>
      </Show>
      <Show when={!props.hideValidation && getFormErrors() !== false}>
        <p class="text-sm text-error-500 lg:col-span-2 lg:col-start-2">
          <For each={getFormErrors()}>{(error) => <p>{error}</p>}</For>
        </p>
      </Show>
    </div>
  );
}

export default Select;
export const SelectLoader = () => {
  return (
    <Select value={1 as never} options={[{ value: 1, name: "Loading..." }]} />
  );
};
